
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import useModuleProgress from '../hooks/useModuleProgress';
import '../styles/pages/_module-pages.scss';
import '../styles/pages/_quiz-completion.scss';
import '../styles/pages/_quiz.scss';

const quizQuestions = [
  {
    question: 'What is the first step to increasing satisfaction?',
    options: [
      'Making money',
      'Awareness of current state',
      'Changing jobs',
      'Moving cities',
      'Buying new things'
    ],
    correctAnswer: 1,
    explanation: 'Awareness of your current state is the first step to increasing satisfaction.'
  },
  {
    question: 'What does "legendarial well-being" focus on?',
    options: [
      'Creating a meaningful legacy and life satisfaction',
      'Achieving fame and recognition',
      'Accumulating material possessions',
      'Social media influence'
    ],
    correctAnswer: 0,
    explanation: 'Legendarial well-being focuses on creating a meaningful legacy through purposeful living, personal growth, and contributing to something larger than yourself.'
  },
  {
    question: 'Which factor contributes most to long-term life satisfaction?',
    options: [
      'Strong relationships and social connections',
      'Financial success',
      'Physical appearance',
      'Social media followers'
    ],
    correctAnswer: 0,
    explanation: 'Research consistently shows that strong relationships and social connections are the strongest predictors of long-term life satisfaction and happiness.'
  },
  {
    question: 'What role does purpose play in well-being?',
    options: [
      'It provides direction and meaning to daily activities',
      'It helps you achieve career success',
      'It increases your social status',
      'It helps you make more money'
    ],
    correctAnswer: 0,
    explanation: 'Purpose provides direction and meaning to life, helping individuals connect their daily activities to broader goals and values, leading to greater fulfillment and satisfaction.'
  },
  {
    question: 'How does gratitude practice affect well-being?',
    options: [
      'It increases positive emotions and life satisfaction',
      'It helps you get more gifts',
      'It improves your social status',
      'It increases your wealth'
    ],
    correctAnswer: 0,
    explanation: 'Regular gratitude practice has been shown to increase positive emotions, improve relationships, enhance physical health, and boost overall life satisfaction.'
  }
];

const LegendarialModule1QuizPage: React.FC = () => {
  const navigate = useNavigate();
  const { currentStep, setCurrentStep, isLoading, saveProgress } = useModuleProgress('legendarialM1');
  const [currentQuestion, setCurrentQuestion] = useState<number>(0);
  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [responses, setResponses] = useState<{[key: number]: number}>({});
  const [showCompletion, setShowCompletion] = useState(false);

  // Always restore selectedAnswer and showExplanation from responses when currentQuestion changes
  useEffect(() => {
    if (responses[currentQuestion] !== undefined) {
      setSelectedAnswer(responses[currentQuestion]);
      setShowExplanation(true);
    } else {
      setSelectedAnswer(null);
      setShowExplanation(false);
    }
  }, [currentQuestion, responses]);

  const handleAnswerSelect = (answerIndex: number) => {
    setSelectedAnswer(answerIndex);
    setShowExplanation(true);
    setResponses(prev => ({...prev, [currentQuestion]: answerIndex}));
  };

  const handleNext = () => {
    if (currentQuestion < quizQuestions.length - 1) {
      const nextQuestion = currentQuestion + 1;
      setCurrentQuestion(nextQuestion);
      setSelectedAnswer(null);
      setShowExplanation(false);
      saveProgress(nextQuestion + 1);
    } else {
      saveProgress(quizQuestions.length, true);
      setShowCompletion(true);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };

  const correctAnswers = Object.values(responses).filter((answer, index) =>
    answer === quizQuestions[index].correctAnswer
  ).length;

  if (isLoading) {
    return (
      <div className="module-form" style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (showCompletion) {
    return (
      <div className="quiz-completion-section">
        <h2 className="completion-title">
          <span>Congratulations!</span>
        </h2>
        <p className="completion-subtitle">
          You've successfully completed the Module 1 quiz!
        </p>
        <div className="completion-card">
          <div className="achievement-section">
            <h3 className="section-title">
              <span>Key Achievements</span>
            </h3>
            <p className="achievement-intro">You've demonstrated a great understanding of:</p>
            <ul className="achievement-list">
              <li>
                <span className="check"></span>
                <span>The foundations of legendarial well-being</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Personal meaning and satisfaction strategies</span>
              </li>
              <li>
                <span className="check"></span>
                <span>Consistency in legacy-building habits</span>
              </li>
            </ul>
          </div>
          <div className="next-steps-section">
            <h3 className="section-title">
              <span>Next Steps</span>
            </h3>
            <p>Continue your journey by reflecting and acting on your values!</p>
            <p>Ready to deepen your understanding of meaning and fulfillment?</p>
          </div>
          <button
            className="resource-button"
            onClick={() => navigate('/panel/learn/legendarial-resources')}
          >
            Return to Legendarial Resources
          </button>
        </div>
      </div>
    );
  }

  const currentQuizQuestion = quizQuestions[currentQuestion];
  const progress = ((currentQuestion + 1) / quizQuestions.length) * 100;

  return (
    <div className="module-form">
      <div className="module-container">
        <div className="module-left legendarial-panel">
          <div className="module-header">
            <h1>Module 1 Quiz</h1>
            <p className="module-subtitle">Understanding Life Satisfaction</p>
            <hr className="module-divider" />
            <p className="module-tool-info">
              Let's review what we've learned in a stress-free way.<br/>
              There's no scoring - this is just to reinforce the main ideas!
            </p>
          </div>
        </div>
        <div className="assessment-right-panel">
          <div className="assessment-content">
            <div className="module-progress">
              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${progress}%` }}></div>
              </div>
              <span className="progress-text">
                Question {currentQuestion + 1} of {quizQuestions.length}
              </span>
            </div>
            <div className="question-section">
              <h2 className="question-title">
                {currentQuizQuestion.question}
              </h2>
              <p className="question-instruction">
                Please select the answer that best matches your understanding.
              </p>
              <div className="answer-options">
                {currentQuizQuestion.options.map((option, index) => (
                  <label key={index} className="answer-option">
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={index}
                      checked={selectedAnswer === index}
                      onChange={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                    />
                    <span className="option-text">{option}</span>
                  </label>
                ))}
              </div>
              {showExplanation && (
                <div className="explanation">
                  <h3>{selectedAnswer === currentQuizQuestion.correctAnswer ? 'Correct!' : 'Remember:'}</h3>
                  <p>{currentQuizQuestion.explanation}</p>
                </div>
              )}
            </div>
            <div className="navigation-buttons">
              <button
                className="btn-secondary"
                onClick={currentQuestion === 0 ? () => navigate('/panel/learn/legendarial-resources') : handleBack}
              >
                {currentQuestion === 0 ? 'Exit Quiz' : 'Previous Question'}
              </button>
              {currentQuestion < quizQuestions.length - 1 ? (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Next Question
                </button>
              ) : (
                <button
                  className="btn-primary"
                  onClick={handleNext}
                  disabled={responses[currentQuestion] === undefined}
                >
                  Complete Quiz
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LegendarialModule1QuizPage;


